import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Progress } from '../ui';
import { formatMoneyWhole, formatPercent } from '../../lib/format';
import ContractBreakdownSummary from '../ContractBreakdownSummary';

/**
 * Single-row KPI strip for the project-detail chrome. Replaces the
 * legacy 8-card grid (Contract / SOV / Billed / Remaining / Variance /
 * Days / Collected / Outstanding).
 *
 * Per Schedule handoff §3 "KPI Strip":
 *   - 56px min height, 10px/24px padding
 *   - Each cell: small uppercase label + mono value
 *   - **Empty-state muting**: when a value is 0 / $0 / 0%, BOTH the label
 *     and the value drop to --text-4 so populated metrics breathe.
 *   - 18px gap between cells with a 1px × 28px --border-subtle divider
 *   - Inline Billed Progress on the right (180px wide)
 *   - Retainage Held surfaces inline next to the Billed to date cell
 *     (orange `--warning` tone) when there's any retainage withheld.
 *
 * Pass any value as null/undefined/0 to trigger the muted empty state.
 *
 * Note: the original spec had an expand chevron + 4-tile drawer (Burn
 * rate / Retainage held / This month billed / Forecast at completion).
 * Removed 2026-05-28 — all four values were $0 on every project and
 * the affordance was carrying state for no observable benefit.
 */
export default function KpiStrip({
  // Primary cells
  contractValue,
  billedToDate,
  remaining,
  daysRemaining,
  collectedToDate,
  outstanding,
  // Used only for the inline retainage annotation next to Billed to date
  retainageHeld,
  // Inline billed progress
  billedPercent,
  // Optional handler for Collected (click → edit, parent decides UX)
  onEditCollected,
  // The contract breakdown (useContractBreakdown) -- when there is one, Contract value drops it down on hover -- and
  // where its "Open SOV tab" goes.
  contractBreakdown,
  onOpenSov,
}) {
  // Inline retainage annotation next to Billed to date. Only render
  // when there's actually retainage withheld -- a $0 retainage line
  // would just be noise. Orange text reuses the warning token so it
  // pops against the neutral KPI value without screaming.
  const retainageInline = (Number(retainageHeld) || 0) > 0
    ? `+ ${formatMoneyWhole(retainageHeld)} retainage`
    : null;

  const cells = [
    { label: 'Contract value', value: contractValue, mono: true, money: true },
    // 'SOV allocated' tile removed 2026-06-01 — equals Contract value on a
    // fully-allocated SOV and is noise for day-to-day billing. The
    // sovAllocated prop was dropped from the signature too; callers may
    // still pass it (ignored) without harm.
    {
      label: 'Billed to date',
      value: billedToDate,
      mono: true,
      money: true,
      annotation: retainageInline,
      annotationColor: 'var(--warning)',
    },
    { label: 'Remaining', value: remaining, mono: true, money: true },
    { label: 'Days remaining', value: daysRemaining, mono: true },
    {
      label: 'Collected',
      value: collectedToDate,
      mono: true,
      money: true,
      onClick: onEditCollected,
    },
    { label: 'Outstanding', value: outstanding, mono: true, money: true },
  ];

  return (
    <div style={{
      background: 'var(--bg-surface)',
      // Rounded card on --bg-page. overflow: hidden clips the inner
      // main row's overflow-x: auto scrollbar to the rounded shape
      // (and the expanded drawer's bottom-border to the corners).
      // KpiStrip's outer wrapper isn't sticky so this doesn't break
      // sticky positioning anywhere.
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Main row: 46px (64 until Duncan 2026-10-27: the top of the page took
          "almost half of the screen") -- 13px label + 2px gap + 18px value + 6px
          vertical padding. */}
      <div
        style={{
          minHeight: 46,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: '6px 32px',
          // No bottom border here — the outer card's border + rounded
          // corners (added on the outermost div) handle the boundary.
          // When expanded, the dashed border on top of the drawer is
          // the only divider needed.
          borderBottom: 'none',
          fontFamily: 'var(--font-sans)',
          // 7 KPI cells + dividers + 180px billed-progress + 28px
          // chevron has a min-width around 1300-1500px. Cell labels +
          // money values use whiteSpace: nowrap so they refuse to
          // shrink. Without overflow handling here, the strip would
          // push the entire chrome wider than the viewport on common
          // laptop sizes, clipping the right edge of every chrome
          // surface (project header, last tab, end of the strip).
          // overflow-x: auto keeps the chrome capped at viewport width
          // and lets users scroll inside the strip to reach the cells
          // that don't fit.
          overflowX: 'auto', overflowY: 'visible',
        }}
      >
        {cells.map((cell, i) => (
          <React.Fragment key={cell.label}>
            {cell.label === 'Contract value' && contractBreakdown
              ? <ContractValueDrill cell={cell} breakdown={contractBreakdown} onOpenSov={onOpenSov} />
              : <Cell {...cell} />}
            {i < cells.length - 1 && <Divider />}
          </React.Fragment>
        ))}

        {/* Inline Billed Progress — pushed to the far right via auto
            margin so on wide screens the 8 cells stay grouped on the
            left and the progress + chevron sit flush against the right
            edge, per handoff §3 ("Inline Billed Progress at the right"). */}
        {typeof billedPercent === 'number' && (
          <>
            <div style={{ marginLeft: 'auto' }} />
            <Divider />
            <div style={{ width: 180, flexShrink: 0 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--text-3)',
                  marginBottom: 4,
                }}
              >
                <span>Billed</span>
                <span className="mono" style={{ color: 'var(--text-2)' }}>
                  {formatPercent(billedPercent)}
                </span>
              </div>
              <Progress value={billedPercent} height={4} />
            </div>
          </>
        )}

      </div>
      {/* Removed: expand chevron + 4-tile drawer (Burn rate / Retainage
          held / This month billed / Forecast at completion). All four
          values were $0 on every project we tested and the chrome was
          carrying state + a noisy affordance for them. Retainage Held
          still surfaces inline next to the Billed to date cell via
          `retainageInline`. If the drawer comes back, restore the
          chevron button + the `{expanded && (...)}` block here. */}
    </div>
  );
}

/* -------------------- contract value drop-down -------------------- */

/* Contract value drops down what makes up the contract on file (Duncan 2026-11-03), as the Job Cost tiles' Travel and
   Risk summaries do: hover previews it, a click pins it, Esc / a click outside / a scroll closes it. It's drawn on
   document.body at the cell's spot -- the strip clips what overflows it. */
function ContractValueDrill({ cell, breakdown, onOpenSov }) {
  const ref = useRef(null);
  const timer = useRef(null);
  const [open, setOpen] = useState(null); // null | 'hover' | 'pinned'
  const [pos, setPos] = useState(null);
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => () => clear(), []);
  const show = (mode) => {
    const r = ref.current && ref.current.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.left - 6, window.innerWidth - 416)) });
    setOpen(mode);
  };
  const enter = () => { clear(); if (open !== 'pinned') timer.current = setTimeout(() => show(open || 'hover'), 120); };
  const leave = () => { clear(); if (open === 'hover') timer.current = setTimeout(() => setOpen((o) => (o === 'hover' ? null : o)), 180); };
  const toggle = () => { clear(); if (open === 'pinned') setOpen(null); else show('pinned'); };
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!(e.target instanceof Element) || !e.target.closest('[data-contract-drill]')) setOpen(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    const onMove = () => setOpen(null);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);
  return (
    <>
      <div ref={ref} data-contract-drill="" role="button" tabIndex={0} aria-haspopup="dialog" aria-expanded={!!open}
        title="What makes up the contract value"
        onMouseEnter={enter} onMouseLeave={leave} onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        style={{
          cursor: 'pointer', borderRadius: 6, padding: '4px 6px', margin: '-4px -6px', flexShrink: 0,
          background: open ? 'var(--bg-hover)' : 'transparent', transition: 'background .12s',
        }}>
        <Cell {...cell} chevron open={!!open} />
      </div>
      {open && pos && createPortal(
        <div data-contract-drill="">
          <ContractBreakdownSummary breakdown={breakdown}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1000 }}
            onMouseEnter={clear} onMouseLeave={leave} onClose={() => setOpen(null)}
            onOpenSov={onOpenSov ? () => { setOpen(null); onOpenSov(); } : undefined} />
        </div>,
        document.body,
      )}
    </>
  );
}

/* -------------------- cell -------------------- */

function Cell({ label, value, mono, money, onClick, annotation, annotationColor, chevron, open }) {
  const empty = !isPopulated(value);
  // Label stays at text-2 (more contrast than spec's text-3 — needed
  // for dark-mode legibility at small font sizes). Value gets the
  // text-4 muting when empty so the "populated values pop" intent
  // still lands.
  const labelColor = 'var(--text-2)';
  const valueColor = empty ? 'var(--text-4)' : 'var(--text-1)';
  // Full money format (e.g. $73,857 instead of $74k). Plenty of
  // horizontal room in the KPI strip at typical widths, and full
  // values match what shows in the AFP detail header below — keeps
  // the user from having to mentally map "$74k" to "$73,857". Whole
  // dollars, though: cents are noise up here (Duncan 2026-10-04);
  // the tables below keep them.
  const displayValue = money ? formatMoneyWhole(value || 0) : String(value ?? '—');

  const content = (
    <>
      <div
        style={{
          display: 'block',
          fontSize: 11.5,
          lineHeight: '13px',
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: labelColor,
          whiteSpace: 'nowrap',
          marginBottom: 2,
        }}
      >
        {label}
        {chevron && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            style={{ marginLeft: 4, verticalAlign: '-1px', color: open ? 'var(--accent-text)' : 'var(--text-4)', transition: 'transform 160ms ease', transform: open ? 'rotate(180deg)' : 'none' }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </div>
      {/* Value row: main number + optional inline annotation on the
          same line so the cell stays one-line-tall (no awkward stacked
          look). Annotation sits to the right of the value with a small
          gap, in --warning orange by default. Only renders when
          annotation is truthy AND the parent value is populated. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          whiteSpace: 'nowrap',
        }}
      >
        <span
          className={mono ? 'mono' : undefined}
          style={{
            fontSize: 15,
            lineHeight: '18px',
            fontWeight: 500,
            color: valueColor,
            letterSpacing: '-0.02em',
          }}
        >
          {displayValue}
        </span>
        {annotation && !empty && (
          <span
            className={mono ? 'mono' : undefined}
            style={{
              fontSize: 12,
              lineHeight: '14px',
              fontWeight: 500,
              color: annotationColor || 'var(--text-3)',
              letterSpacing: '-0.005em',
            }}
          >
            {annotation}
          </span>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          padding: '4px 6px',
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          borderRadius: 6,
          flexShrink: 0,
          margin: '-4px -6px',
          transition: 'background .12s',
        }}
        title="Click to update"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {content}
      </button>
    );
  }

  return (
    // flexShrink 0: a squeezed cell's nowrap text ran into its neighbours; the row's
    // overflow-x: auto is what handles narrow screens.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
      {content}
    </div>
  );
}

function Divider() {
  return (
    <span
      style={{
        width: 1,
        height: 24,
        background: 'var(--border-subtle)',
        flexShrink: 0,
      }}
    />
  );
}

/** Treats 0, null, undefined, empty string as empty/muted. */
function isPopulated(v) {
  if (v === null || v === undefined || v === '') return false;
  const n = Number(v);
  if (Number.isFinite(n) && n === 0) return false;
  return true;
}
